package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.i18n.tr
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

enum class LAlertVariant {
    INFO,
    SUCCESS,
    WARNING,
    DANGER,
}

private fun LAlertVariant.color(palette: SitePalette): Color = when (this) {
    LAlertVariant.INFO -> palette.info
    LAlertVariant.SUCCESS -> palette.success
    LAlertVariant.WARNING -> palette.warning
    LAlertVariant.DANGER -> palette.danger
}

/**
 * The shape only. Every colour on this component is the variant's, applied by the composable.
 *
 * **The 4px left rule is gone, and the reason it existed is still satisfied.** It was added so the
 * status survived greyscale rather than depending on telling amber from green — a real requirement,
 * WCAG 1.4.1. But this component has carried a per-variant icon since it was written: info, check,
 * warning and danger are four different shapes, legible with no colour at all and more specific
 * than a coloured stripe ever was. The rule was a second answer to a question already answered, and
 * a bar down the left edge of a tinted box is the most tired shape in interface design.
 *
 * What remains is a uniform hairline, a weak tinted ground, and the icon doing the work.
 */
val LAlertStyle = CssStyle.base {
    Modifier
        .borderRadius(Radius.Card)
        .padding(Space.LG)
}

/**
 * The dismiss control, sized to the hit-area floor rather than to its glyph.
 *
 * The same shape as the toast's close button and for the same reason: this is the control someone
 * reaches for to clear a banner they have already read, often while the page under it is still
 * settling, so a 16px target would be the hardest thing on the screen to hit. The glyph stays at
 * [IconSize.MD]; the target is 40px around it.
 */
val LAlertDismissStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Color.rgba(0, 0, 0, 0f))
            .color(colorMode.toSitePalette().textSecondary)
            .border(1.px, LineStyle.Solid, Color.rgba(0, 0, 0, 0f))
            .borderRadius(Radius.Control)
            .size(HIT_AREA_FLOOR_PX.px)
            .styleModifier { property("display", "inline-flex") }
            .alignItems(AlignItems.Center)
            .justifyContent(JustifyContent.Center)
            .padding(0.px)
            .cursor(Cursor.Pointer)
    }
    hover { Modifier.color(colorMode.toSitePalette().textPrimary) }
    focusVisible {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
}

/**
 * How much of the variant's hue reaches the ground and the edge.
 *
 * The ground is deliberately weak — the rule does the work — and the edge sits between the two so
 * the card still has a boundary where a tint alone would leave it floating.
 */
private const val ALERT_GROUND_TINT = 0.06f
private const val ALERT_EDGE_TINT = 0.40f

/**
 * A notice, marked by its icon rather than by a stripe.
 *
 * **[live] defaults to off, and that default is the fix for a real defect.** The role used to be
 * unconditional — `alert` for `DANGER`, `status` otherwise — so opening `/app/settings/billing`
 * while past due fired an assertive live region during page load. `billing.md` names exactly this:
 * "On first paint it carries **no live role** — a `role="alert"` present at load steals the screen
 * reader before the page is read." A notice that was already on the page when it loaded is read in
 * document order like everything else; interrupting to announce it means the reader hears the
 * banner before the `<h1>` that says which page they are on.
 *
 * Pass `live = true` only for a notice that **appears in response to something the reader just
 * did** — a save that failed, a plan that changed under them. That is the case a live region is
 * for, and it is the only case where the interruption is information rather than noise.
 *
 * The title is an `<h2>` and the body a `<p>` rather than two `Div`s, so the notice is a landmark
 * a screen reader can jump to and skip past. It keeps `body/15-strong` rather than taking
 * `card/h2`'s 21/700: this is the design system's alert, used on eleven surfaces, and only
 * billing's banner is specified at heading size. The element is the part that was wrong.
 *
 * **[description] is nullable because a one-clause notice is a real thing and had been faking it.**
 * Callers with nothing to add were passing `""`, which still rendered a `<p>` — an empty paragraph
 * inside the landmark, taking the column's gap and announcing a blank line. A notice with no second
 * sentence now emits no second element.
 *
 * **[dense] is the shell-banner shape**, and it is a different component in every way that matters
 * to a reader: one row, `Space.MD` of padding, the title inline with its own action, and no `<p>`
 * at all — the description, when there is one, runs on after the title the way the rest of that
 * sentence would. A banner pinned above the whole application is read at a glance on the way to
 * somewhere else, and the full alert's two-line block is a page-level object being asked to do a
 * chrome-level job.
 *
 * [onDismiss] is absent by default rather than always-on, because most of these notices are facts
 * about the page's state — a past-due plan, a read-only project — and a fact you can dismiss
 * without changing anything is a control that lies. Pass it for a notice the reader can genuinely
 * be finished with.
 */
@Composable
fun LAlert(
    title: String,
    description: String? = null,
    modifier: Modifier = Modifier,
    variant: LAlertVariant = LAlertVariant.INFO,
    action: (@Composable () -> Unit)? = null,
    live: Boolean = false,
    dense: Boolean = false,
    onDismiss: (() -> Unit)? = null,
) {
    val colorMode = ColorMode.current
    val palette = colorMode.toSitePalette()
    val tone = variant.color(palette)
    Row(
        LAlertStyle.toModifier()
            .then(if (dense) Modifier.padding(Space.MD) else Modifier)
            .backgroundColor(tone.tintOn(palette.surface, ALERT_GROUND_TINT))
            // One hairline, all four sides, in the variant's hue at 40%. No side is special.
            .border(1.px, LineStyle.Solid, tone.tintOn(palette.surface, ALERT_EDGE_TINT))
            .then(
                if (live) {
                    Modifier.role(if (variant == LAlertVariant.DANGER) "alert" else "status")
                } else {
                    Modifier
                },
            )
            // The shared 6px rise. An alert almost always arrives in answer to something — a save
            // that failed, a refusal, a limit reached — and appearing instantly reads as though it
            // had been there all along and been missed. Transform only, and not applied at all
            // under a reduced-motion preference, so nothing here can delay or hide the message.
            .enterRiseUnlessOwned()
            .then(if (dense) Modifier.attr("data-alert-dense", "true") else Modifier)
            // **The banner has to reflow, not compress.** The action is `flex: 0 0 auto` and the
            // glyph is too, so on a 390 viewport the fixed 140px button and four paddings left the
            // message column about 90px wide: a twelve-word shell banner set one word per line over
            // eight lines, taking the whole first screen above the page title. Wrapping lets the
            // action drop under a full-width message instead — see the `flex: 1 1 …` basis on the
            // text column below, which is what decides where it breaks.
            .flexWrap(FlexWrap.Wrap)
            // Only when it has wrapped: `row-gap` costs nothing on the single-line layout.
            .styleModifier { property("row-gap", Space.MD.toString()) }
            .then(modifier),
        // Dense centres against its single row; the full alert hangs its glyph from the first line
        // of a block that may run to three.
        verticalAlignment = if (dense) Alignment.CenterVertically else Alignment.Top,
    ) {
        Div(
            Modifier
                .color(tone)
                .then(if (dense) Modifier else Modifier.padding(top = Space.XXS))
                .styleModifier { property("flex", "0 0 auto") }
                .toAttrs(),
        ) {
            when (variant) {
                LAlertVariant.INFO -> LInfoIcon()
                LAlertVariant.SUCCESS -> LCheckIcon()
                LAlertVariant.WARNING -> LWarningIcon()
                LAlertVariant.DANGER -> LDangerIcon()
            }
        }
        if (dense) {
            // One row: the description is a run of text after the title, not a block under it, so
            // the banner keeps its height whether or not it has a second clause.
            Row(
                Modifier
                    // A real basis, not `flex-grow` alone. With `flex-basis: auto` the message
                    // column shrinks to whatever the fixed-width action leaves it; 240px is the
                    // width below which the outer row wraps instead, which is the behaviour the
                    // small breakpoint needs.
                    .styleModifier { property("flex", "1 1 240px") }
                    .gap(Space.SM)
                    .flexWrap(FlexWrap.Wrap)
                    .padding(left = Space.MD),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                LAlertTitle(palette, title)
                if (!description.isNullOrBlank()) {
                    Span(
                        Modifier
                            .color(palette.textSecondary)
                            .fontSize(TypeScale.BodySmall)
                            .lineHeight(TypeScale.BodyLineHeight)
                            .toAttrs(),
                    ) {
                        Text(description)
                    }
                }
            }
        } else {
            Column(
                Modifier
                    .styleModifier { property("flex", "1 1 240px") }
                    .gap(Space.XS)
                    .padding(left = Space.MD),
            ) {
                // `H2`/`P`, not two `Div`s. The margin resets are load-bearing: a bare `<h2>` and
                // `<p>` bring the user-agent's own margins with them, which would push the rule out
                // of alignment with the text it belongs to.
                LAlertTitle(palette, title)
                if (!description.isNullOrBlank()) {
                    P(
                        Modifier
                            .color(palette.textSecondary)
                            .fontSize(TypeScale.BodySmall)
                            .lineHeight(TypeScale.BodyLineHeight)
                            .margin(0.px)
                            .toAttrs(),
                    ) {
                        Text(description)
                    }
                }
            }
        }
        if (action != null) {
            Div(
                Modifier
                    .padding(left = Space.LG)
                    .styleModifier { property("flex", "0 0 auto") }
                    .toAttrs(),
            ) {
                action()
            }
        }
        if (onDismiss != null) {
            Div(
                Modifier
                    .padding(left = Space.SM)
                    .styleModifier { property("flex", "0 0 auto") }
                    .toAttrs(),
            ) {
                Button(
                    LAlertDismissStyle.toModifier().toAttrs {
                        type(ButtonType.Button)
                        attr("aria-label", tr("ds.ds.lalert.dismiss_this_notice", "Dismiss this notice"))
                        onClick { onDismiss() }
                    },
                ) {
                    LCloseIcon(Modifier.iconSize(IconSize.MD))
                }
            }
        }
    }
}

@Composable
private fun LAlertTitle(palette: SitePalette, title: String) {
    H2(
        Modifier
            .color(palette.textPrimary)
            .fontSize(TypeScale.BodySmall)
            .fontWeight(600)
            .lineHeight(TypeScale.BodyLineHeight)
            .margin(0.px)
            .toAttrs(),
    ) {
        Text(title)
    }
}
